import type { SxProps, Theme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { FindingVisualTheme } from '../../lib/employerReview/findingVisualTheme';
import type { ReviewReasonSeverity } from '../../lib/employerReview/reviewReason.types';

export type ConcernAccent = {
  stripe: string;
  softBg: string;
  border: string;
};

export function resolveConcernAccent(
  visual: FindingVisualTheme | null,
  severity: ReviewReasonSeverity,
  theme: Theme,
  isLight: boolean,
): ConcernAccent {
  if (visual) {
    return {
      stripe: visual.accent,
      softBg: alpha(visual.accent, isLight ? 0.06 : 0.12),
      border: alpha(visual.accent, isLight ? 0.24 : 0.34),
    };
  }
  if (severity === 'BLOCKER') {
    return {
      stripe: theme.palette.error.main,
      softBg: alpha(theme.palette.error.main, isLight ? 0.05 : 0.1),
      border: alpha(theme.palette.error.main, isLight ? 0.22 : 0.3),
    };
  }
  if (severity === 'CONCERN') {
    return {
      stripe: theme.palette.warning.dark,
      softBg: alpha(theme.palette.warning.main, isLight ? 0.06 : 0.1),
      border: alpha(theme.palette.warning.main, isLight ? 0.26 : 0.32),
    };
  }
  return {
    stripe: theme.palette.info.main,
    softBg: alpha(theme.palette.info.main, isLight ? 0.04 : 0.08),
    border: alpha(theme.palette.divider, isLight ? 1 : 0.6),
  };
}

export function concernAccordionShellSx(
  accent: ConcernAccent,
  options?: { nested?: boolean; neutral?: boolean },
): SxProps<Theme> {
  const nested = options?.nested ?? false;
  const neutral = options?.neutral ?? false;
  const radius = nested ? 10 : 12;

  return (theme) => {
    const isLight = theme.palette.mode === 'light';
    const collapsedShadow = `0 1px 3px ${alpha(theme.palette.common.black, isLight ? 0.05 : 0.2)}`;
    const expandedShadow = `0 6px 18px ${alpha(
      neutral ? theme.palette.common.black : accent.stripe,
      isLight ? 0.08 : 0.18,
    )}`;

    return {
      borderRadius: `${radius}px !important`,
      border: '1px solid',
      borderColor: neutral ? 'divider' : accent.border,
      borderLeftWidth: neutral ? 1 : 4,
      borderLeftColor: neutral ? 'divider' : accent.stripe,
      bgcolor: 'background.paper',
      overflow: 'hidden',
      boxShadow: collapsedShadow,
      transition: theme.transitions.create(['box-shadow', 'border-color'], { duration: 220 }),
      '&:before': { display: 'none' },
      '&.Mui-expanded': {
        margin: 0,
        boxShadow: expandedShadow,
      },
    };
  };
}

export function concernAccordionSummarySx(accent: ConcernAccent): SxProps<Theme> {
  return {
    minHeight: 48,
    px: 1.5,
    py: 0.35,
    bgcolor: 'transparent',
    transition: 'background-color 0.2s ease',
    '&:hover': {
      bgcolor: (theme) => alpha(theme.palette.action.hover, 0.55),
    },
    '&.Mui-expanded': {
      bgcolor: accent.softBg,
      '&:hover': { bgcolor: accent.softBg },
    },
    '& .MuiAccordionSummary-content': { my: 0.55, alignItems: 'center' },
    '& .MuiAccordionSummary-expandIconWrapper': {
      color: 'text.secondary',
      transition: 'transform 0.22s ease',
    },
  };
}

export function concernAccordionDetailsSx(): SxProps<Theme> {
  return {
    px: 1.5,
    pt: 0,
    pb: 1.35,
    borderTop: '1px solid',
    borderColor: (theme) => alpha(theme.palette.divider, 0.85),
  };
}

export function concernIndexBadgeSx(accent: ConcernAccent): SxProps<Theme> {
  return {
    width: 22,
    height: 22,
    borderRadius: '50%',
    flexShrink: 0,
    display: 'grid',
    placeItems: 'center',
    fontSize: '0.75rem',
    fontWeight: 800,
    letterSpacing: 0,
    color: accent.stripe,
    bgcolor: (theme) => alpha(accent.stripe, theme.palette.mode === 'light' ? 0.1 : 0.18),
    border: '1px solid',
    borderColor: (theme) => alpha(accent.stripe, theme.palette.mode === 'light' ? 0.22 : 0.32),
  };
}

export function severityChipSx(
  _color: 'error' | 'warning' | 'info',
): SxProps<Theme> {
  return {
    height: 22,
    fontWeight: 800,
    fontSize: '0.75rem',
    borderRadius: '999px',
    '& .MuiChip-label': { px: 1 },
  };
}

export function reviewerActionCalloutSx(isLight: boolean): SxProps<Theme> {
  return (theme) => ({
    p: 1,
    borderRadius: 2,
    border: 'none',
    bgcolor: isLight ? alpha(theme.palette.primary.main, 0.04) : alpha(theme.palette.primary.light, 0.08),
    borderLeft: '3px solid',
    borderLeftColor: alpha(theme.palette.primary.main, 0.35),
  });
}

export function neutralGroupAccordionSx(): SxProps<Theme> {
  return (theme) => ({
    borderRadius: '12px !important',
    border: '1px dashed',
    borderColor: 'divider',
    bgcolor: alpha(theme.palette.action.hover, theme.palette.mode === 'light' ? 0.35 : 0.2),
    overflow: 'hidden',
    '&:before': { display: 'none' },
    '&.Mui-expanded': { margin: 0 },
  });
}
